import { IconButton, LuIcon, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import type { Procedure, ProcedureType, Stored } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { ProcedureTypeIcon } from "../../Icons/ProcedureTypeIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

interface ProcedureTypeTableColumnsConfig {
	procedureTypes: Stored<ProcedureType>[]
	procedures: Stored<Procedure>[]
	onView: (procedureType: Stored<ProcedureType>) => void
}

export const useProcedureTypeTableColumns = (config: ProcedureTypeTableColumnsConfig) => {
	const { procedureTypes, procedures, onView } = config

	return useMemo<ColumnDef<Stored<ProcedureType>>[]>(() => {
		const procedureCountByProcedureTypeId: Record<ProcedureType["uuid"], number> = {}

		for (const procedureType of procedureTypes) {
			procedureCountByProcedureTypeId[procedureType.uuid] = 0
		}

		for (const procedure of procedures) {
			procedureCountByProcedureTypeId[procedure.procedure_type] =
				(procedureCountByProcedureTypeId[procedure.procedure_type] ?? 0) + 1
		}

		return [
			{
				id: "name",
				accessorKey: "name",
				enableHiding: true,
				meta: {
					className: "w-1/2",
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const procedureType = row.original

					return (
						<div className="flex items-center gap-2">
							<ProcedureTypeIcon procedureTypeId={procedureType.uuid} background={false} />
							<Text>{procedureType.name}</Text>
						</div>
					)
				},
			},
			{
				id: "procedure_count",
				accessorFn: (procedureType) => procedureCountByProcedureTypeId[procedureType.uuid] ?? 0,
				meta: {
					className: "w-1/2",
					plainHeader: "Count",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Count
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{getValue<number>()}</Text>
				},
			},
			{
				id: "more",
				enableHiding: false,
				header: "",
				cell: function Cell({ row }) {
					const procedureType = row.original

					const handleView = useCallback(() => {
						onView(procedureType)
					}, [procedureType])

					return (
						<Menu.Root align="end">
							<Menu.ClickTrigger>
								<IconButton size="sm" variant="ghost" accentColor="base" onClick={stopPropagation}>
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onSelect={handleView}>
									<LuIcon icon={modelIconMapping.expand} /> View
								</Menu.Item>
							</Menu.Content>
						</Menu.Root>
					)
				},
			},
		]
	}, [procedureTypes, procedures, onView])
}

export const useProcedureTypeTable = (args: Omit<TableOptions<Stored<ProcedureType>>, "getCoreRowModel">) => {
	return useReactTable<Stored<ProcedureType>>({
		...args,
		getRowId: (originalRow) => originalRow.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
